import React from "react";
import { Link, useParams } from "react-router-dom";
import { AlertCircle, ChevronLeft, Home, LoaderCircle } from "lucide-react";
import { toast } from "react-toastify";
import MobileViewContainer from "@/Components/MobileViewContainer";
import { Button } from "@/Components/Common/Tailwind";
import {
  GroupReservationAgencyContext,
  GroupReservationPublicConfig,
  GroupReservationRequest,
} from "@/api/types/group-reservation";
import { groupReservationService } from "@/api/services/group-reservation.service";
import {
  buildGroupReservationPayload,
  createInitialGroupReservationForm,
  validateGroupReservationForm,
  GROUP_RESERVATION_ERROR_ORDER,
} from "./groupReservationForm.mjs";
import GroupReservationForm from "./components/GroupReservationForm";
import RequestCompletePanel from "./components/RequestCompletePanel";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";

type LoadState = "loading" | "ready" | "paused" | "disabled" | "invalid-agency" | "error";

const BlockedState = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <div className="flex min-h-[70vh] flex-col justify-center px-5 py-10 text-center">
    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-700">
      <AlertCircle size={26} />
    </div>
    <h1 className="text-xl font-black text-gray-900 text-balance">{title}</h1>
    <p className="mt-3 text-sm leading-6 text-gray-600 text-pretty">{description}</p>
    <Button asChild className="mt-6 h-12 border-emerald-600 bg-emerald-600 text-white transition-transform hover:bg-emerald-700 focus-visible:ring-emerald-600/30 active:scale-[0.98]">
      <Link to="/">홈으로</Link>
    </Button>
  </div>
);

const LoadingState = () => (
  <div className="flex min-h-[70vh] flex-col items-center justify-center gap-3 px-5 text-center text-gray-600">
    <LoaderCircle className="animate-spin text-gray-500" size={34} />
    <p className="text-sm font-semibold">단체 예약 신청 정보를 불러오는 중입니다.</p>
  </div>
);

export default function GroupReservationApplyPage() {
  const { token } = useParams();
  const [config, setConfig] = React.useState<GroupReservationPublicConfig | null>(null);
  const [agencyContext, setAgencyContext] = React.useState<GroupReservationAgencyContext | null>(null);
  const [loadState, setLoadState] = React.useState<LoadState>("loading");
  const [form, setForm] = React.useState(createInitialGroupReservationForm());
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [completeRequest, setCompleteRequest] = React.useState<GroupReservationRequest | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);

  useUnsavedChangesGuard({
    isDirty: hasUnsavedChanges && !submitting && !completeRequest,
  });

  React.useEffect(() => {
    let canceled = false;

    async function load() {
      setLoadState("loading");
      try {
        const [configResponse, agencyResponse] = await Promise.all([
          groupReservationService.getPublicConfig(),
          token ? groupReservationService.getAgencyContext(token) : Promise.resolve(null),
        ]);

        if (canceled) return;

        const nextConfig = configResponse.data;
        setConfig(nextConfig);
        setAgencyContext(agencyResponse?.data ?? null);

        if (!nextConfig.enabled) {
          setLoadState("disabled");
        } else if (nextConfig.is_paused) {
          setLoadState("paused");
        } else {
          setLoadState("ready");
        }
      } catch (error: any) {
        if (canceled) return;
        console.error(error);
        setLoadState(token ? "invalid-agency" : "error");
      }
    }

    load();
    return () => {
      canceled = true;
    };
  }, [token]);

  const updateField = React.useCallback((field: string, value: string | boolean | number[] | Record<number, string>) => {
    setHasUnsavedChanges(true);
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleShareLink = React.useCallback(async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "단체 예약 신청",
          url,
        });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("신청 링크를 복사했습니다.");
    } catch (error) {
      console.error(error);
      toast.error("신청 링크를 공유하지 못했습니다.");
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validation = validateGroupReservationForm(form);
    if (!validation.valid) {
      setErrors(validation.errors);
      toast.warning("필수 입력값을 확인해주세요.");
      const validationErrors = validation.errors as unknown as Record<string, string>;
      const firstErrorField = GROUP_RESERVATION_ERROR_ORDER.find((field: string) => validationErrors[field]);
      if (firstErrorField) {
        window.requestAnimationFrame(() => {
          const element = document.getElementById(`group-field-${firstErrorField}`);
          if (!element) return;
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          if (typeof (element as HTMLElement).focus === "function") {
            element.focus({ preventScroll: true });
          }
        });
      }
      return;
    }

    try {
      setSubmitting(true);
      const payload = buildGroupReservationPayload(form, { agencyToken: token });
      const response = await groupReservationService.createRequest(payload);
      setHasUnsavedChanges(false);
      setCompleteRequest(response.data);
      toast.success("단체 예약 신청이 접수되었습니다.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || "단체 예약 신청 접수에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const themeStyle = {
    "--color-primary": "#059669",
    "--color-primary-dark": "#047857",
  } as React.CSSProperties;

  // 접수 완료 화면: 중앙 정렬 헤더 + gray-100 배경 + 하단 고정 홈으로 CTA
  if (completeRequest) {
    return (
      <MobileViewContainer>
        <div className="flex min-h-screen flex-col bg-gray-100 break-keep wrap-break-word" style={themeStyle}>
          <header className="sticky top-0 z-30 mx-auto flex h-14 w-full max-w-160 items-center justify-center bg-white px-5 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
            <h1 className="text-base font-black text-gray-900">접수 완료</h1>
          </header>

          <div
            className="flex flex-1 flex-col"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}
          >
            <RequestCompletePanel request={completeRequest} />

            <div className="mt-auto px-5 pb-4 pt-2">
              <div className="rounded-2xl bg-white px-4 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
                <Button
                  asChild
                  className="h-12 w-full text-sm border-emerald-600 bg-emerald-600 text-white shadow-[0_4px_14px_rgba(5,150,105,0.25)] transition-transform hover:bg-emerald-700 focus-visible:ring-emerald-600/30 active:scale-[0.98]"
                >
                  <Link to="/">
                    홈으로
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </MobileViewContainer>
    );
  }

  return (
    <MobileViewContainer>
      <div className="min-h-screen bg-white break-keep wrap-break-word" style={themeStyle}>
        <header className="fixed inset-x-0 top-0 z-30 mx-auto flex h-14 max-w-160 items-center gap-3 border-b border-gray-100 bg-white/95 px-4 backdrop-blur supports-backdrop-filter:bg-white/80">
          <Button asChild variant="ghost" size="icon" aria-label="홈으로 이동" className="transition-transform active:scale-[0.96]">
            <Link to="/">
              <ChevronLeft size={18} />
            </Link>
          </Button>
          <div>
            <h1 className="text-base font-black text-gray-900">단체 예약 신청</h1>
          </div>
        </header>
        {/* fixed 헤더 높이만큼 콘텐츠 공간 확보 */}
        <div className="h-14" aria-hidden="true" />

        {loadState === "loading" ? <LoadingState /> : null}
        {!completeRequest && loadState === "disabled" ? (
          <BlockedState
            title="단체 예약 신청을 운영하지 않습니다"
            description="현재 매장에서 단체 예약 신청 링크를 공개하지 않았습니다."
          />
        ) : null}
        {!completeRequest && loadState === "paused" ? (
          <BlockedState
            title="단체 예약 신청이 일시 중지되었습니다"
            description="사이트 운영 일시정지 중에는 일반 신청과 대리점 링크 신청을 접수할 수 없습니다."
          />
        ) : null}
        {!completeRequest && loadState === "invalid-agency" ? (
          <BlockedState
            title="대리점 링크를 확인할 수 없습니다"
            description="링크가 만료되었거나 현재 매장과 일치하지 않습니다."
          />
        ) : null}
        {!completeRequest && loadState === "error" ? (
          <BlockedState
            title="신청 정보를 불러오지 못했습니다"
            description="잠시 후 다시 시도해주세요."
          />
        ) : null}
        {!completeRequest && loadState === "ready" && config ? (
          <GroupReservationForm
            form={form}
            errors={errors}
            config={config}
            agencyContext={agencyContext}
            submitting={submitting}
            onChange={updateField}
            onSubmit={handleSubmit}
            onShareLink={handleShareLink}
          />
        ) : null}
      </div>
    </MobileViewContainer>
  );
}
